import { styled } from 'linaria/lib/react';

import Marquee from 'react-fast-marquee';
import Grain from '../../UI/Grain';
import StyledButton from '../../UI/Button/styled';
import { COLORS, FONTS } from '../../../styles/constants';
import { BUTTON_STYLES } from '../../UI/Button/constants';

export const StyledGrain = styled(Grain)`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
`;

export const StyledInfoContainer = styled.div<{
  hasWhiteAvatar: boolean;
  hasPaddingBottom: boolean;
  removePaddingLeftRight?: boolean;
  gradientStart?: string | null;
  gradientEnd?: string | null;
  isDark?: boolean;
  showGradient?: boolean;
}>`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  position: relative;
  padding: 79px ${({ removePaddingLeftRight }) => (removePaddingLeftRight ? 0 : 24)}px
    ${({ hasPaddingBottom }) => (hasPaddingBottom ? '48px' : 0)};
  background: ${({ showGradient, gradientEnd, isDark }) =>
    showGradient ? gradientEnd ?? (isDark ? COLORS.black[60] : COLORS.white[60]) : 'transparent'};

  .avatar {
    background-color: ${({ hasWhiteAvatar }) => (hasWhiteAvatar ? COLORS.white[10] : COLORS.brown.light[100])};
  }

  &::before {
    background: ${({ showGradient, gradientStart, gradientEnd, isDark }) =>
      showGradient && gradientStart
        ? `linear-gradient(${gradientStart ?? (isDark ? COLORS.black[60] : COLORS.white[60])} 5%, ${
            gradientEnd ?? (isDark ? COLORS.black[60] : COLORS.white[60])
          } 100%)`
        : 'none'};
  }
`;

export const StyledFullName = styled.span`
  font: 400 28px/120% ${FONTS.primary};
  padding: 16px 0 4px 0;
  text-align: center;
  word-break: break-word;
`;

export const StyledUserInfo = styled.div`
  display: flex;
  flex-direction: column;
  padding-top: 16px;
  font: 14px/140% ${FONTS.secondary};
  text-align: center;
  gap: 4px;
  opacity: 0;
  animation: fadeIn 0.3s cubic-bezier(0.33, 0, 0.3, 1) forwards;
  animation-delay: 0.46s;

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

export const StyledContainer = styled.div<{
  isExpanded?: boolean;
}>`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px 12px;
  text-align: center;
  max-width: 342px;
  border-radius: 8px;
  justify-content: center;
  align-items: center;
  background-color: rgba(255, 255, 255, 0.04);
  overflow: ${({ isExpanded }) => (isExpanded ? 'auto' : 'hidden')};
  cursor: pointer;
  transition: max-height 0.3s ease-in-out;
`;

export const StyledUserInfoMarquee = styled(Marquee)`
  font-size: 14px;
  font-family: inherit;
  font-weight: inherit;
  line-height: inherit;
  max-width: 342px;
  mask-image: linear-gradient(90deg, transparent 0%, white 15%, white 85%, transparent 100%);

  /* iOS-specific fixes */
  @supports (-webkit-touch-callout: none) {
    font-size: 14px !important;
    transform: scale(1);
    -webkit-text-size-adjust: 100%;
  }
`;

export const StyledExpandedText = styled.div`
  width: 100%;
  text-align: left;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 4px;
  word-break: break-word;
  align-self: 'stretch';
`;

export const StyledFollowButtonContainer = styled.div`
  align-items: center;
  display: flex;
  gap: 8px;
  padding-top: 16px;
`;

export const StyledText = styled.span<{ shouldBreak?: boolean }>`
  word-break: ${({ shouldBreak }) => (shouldBreak ? 'break-word' : 'normal')};
  font-weight: 400;
`;

export const StyledLink = styled.button`
  background: none;
  border: none;
  color: inherit;
  cursor: pointer;
  font-family: ${FONTS.secondary};
  font-size: 14px;
  font-weight: 600;
  margin: 0 auto;
  text-decoration: none;
  padding-top: 0;
  padding-bottom: 0;
`;

export const StyledSection = styled.section<{
  fontColor: string;
  fixedHeight: boolean;
  isDark: boolean;
  bgColor?: string;
}>`
  color: ${({ fontColor }) => fontColor};
  min-height: auto;
  position: relative;
  transition: 0.5s all ease-in-out;
  background-color: ${({ bgColor, isDark }) => bgColor ?? (isDark ? COLORS.black[100] : COLORS.white[100])};
`;

export const StyledIcon = styled.span`
  display: inline-block;
  padding: 0 4px;
`;

export const StyledVerifiedIcon = styled.span`
  display: inline-flex;
  padding: 0 4px 0 6px;
  vertical-align: middle;
`;

export const StyledSubtitle = styled.span`
  font: 600 14px/140% ${FONTS.secondary};
`;

export const StyledVerificationWrap = styled.div`
  padding-top: 4px;
`;

export const StyledWrap = styled.span`
  flex-shrink: 0;
`;

export const StyledMessageButton = styled(StyledButton)`
  border: 1px solid ${({ isDark }) => (isDark ? COLORS.white[30] : COLORS.brown.light[100])};
  box-sizing: border-box;

  &:disabled {
    background: ${({ isDark, buttonType }) => BUTTON_STYLES.background[buttonType].default[isDark ? 'dark' : 'light']};
    color: ${({ isDark, buttonType }) => BUTTON_STYLES.color[buttonType].default[isDark ? 'dark' : 'light']};
    cursor: default;
  }
`;

export const StyledHeroArea = styled.div`
  position: relative;
`;

export const StyledTabsContainer = styled.div`
  display: flex;
  padding: 16px 32px 12px 32px;
  gap: 8px;
  justify-content: center;
  align-items: flex-start;
  opacity: 0;
  animation: fadeIn 0.3s cubic-bezier(0.33, 0, 0.3, 1) forwards;
  animation-delay: 0.46s;

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

export const StyledTab = styled.div<{
  isDark?: boolean;
  active?: boolean;
  shouldAnimate?: boolean;
  isTransitioning?: boolean;
  wasActive?: boolean;
  hideInitially?: boolean;
}>`
  --indicator-color: ${({ isDark = true }) => {
    return isDark ? COLORS.white[100] : COLORS.brown.dark[100];
  }};

  color: var(--indicator-color);
  opacity: ${({ active, isTransitioning, wasActive }) => {
    if (isTransitioning && active) return 1;
    if (isTransitioning && wasActive) return 0.6;
    return active ? 1 : 0.6;
  }};
  cursor: pointer;
  font: 600 14px/140% ${FONTS.secondary};
  display: flex;
  flex-direction: column;
  justify-content: center;
  text-align: center;
  align-items: center;
  align-content: center;
  height: 40px;
  padding: 0 12px;
  position: relative;
  transition: opacity 0.8s cubic-bezier(0.5, 0, 0, 1);

  /* Ensure consistent vertical alignment */
  & > * {
    vertical-align: middle;
  }

  .tab-content-wrapper {
    display: inline-flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 3px;
    row-gap: 3px;
    position: relative;
    /* Ensure gap remains fixed during animations */
    min-height: fit-content;
  }

  .tab-label {
    display: inline-block;
    line-height: 1;
    min-height: 1em;
    /* Ensure consistent height during slot reel animation */
    height: 20px;
  }

  .bull-icon-placeholder {
    height: 7px;
    min-height: 7px;
    max-height: 7px;
    display: block;
    visibility: hidden;
    flex-shrink: 0;
  }

  .bull-icon-container {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    width: var(--tab-label-width, 100%);
    max-width: var(--tab-label-width, 100%);
    height: 7px;
    min-height: 7px;
    max-height: 7px;
    flex-shrink: 0;
    margin-top: 0;

    svg {
      position: absolute;
      left: 50%;
      width: 7px;
      height: 7px;
      will-change: transform, opacity;
      transform: translateX(-50%) translateZ(0);
      transform-origin: center center;
      opacity: ${({ shouldAnimate, active, isTransitioning, hideInitially }) => {
        // During transition, hide SVG (::after pseudo-element handles the animation)
        if (isTransitioning && active) return 0;
        // If shouldAnimate is true, keep SVG hidden - it will be shown via animation at 3s
        if (shouldAnimate && active) return 0;
        // If hideInitially is true (initial tab load), hide SVG until animation starts
        if (hideInitially && active) return 0;
        // If tab is active and not animating, show SVG immediately
        if (active) return 1;
        return 0;
      }};
      animation: ${({ shouldAnimate, active, isTransitioning, hideInitially }) => {
        // Show SVG circle after initial animation completes (at 3s - after 2s delay + 1s animation)
        // Use animation to override the initial opacity: 0
        if ((shouldAnimate && active) || (hideInitially && active)) return 'showCircle 0s 3s forwards';
        // During transition, hide then show after transition completes
        if (isTransitioning && active) {
          return 'hideDuringTransition 0s 0.8s forwards, showAfterTransition 0s 1.4s forwards';
        }
        return 'none';
      }};
      animation-fill-mode: forwards;
      backface-visibility: hidden;
      -webkit-backface-visibility: hidden;

      circle {
        fill: var(--indicator-color);
        opacity: 0.7;
      }
    }

    &::before {
      content: '';
      position: absolute;
      bottom: 0;
      left: 50%;
      transform: translateX(-50%);
      width: 0;
      height: 2px;
      max-width: var(--tab-label-width, 100%);
      background-color: var(--indicator-color);
      opacity: 0;
      border-radius: 2px;
      z-index: 1;
      display: ${({ shouldAnimate, active, hideInitially }) =>
        (shouldAnimate && active) || (hideInitially && active) ? 'block' : 'none'};
      pointer-events: none;
      animation: ${({ shouldAnimate, active, hideInitially }) =>
        (shouldAnimate && active) || (hideInitially && active)
          ? 'initialAppear 0.2s 2s cubic-bezier(0.5, 0, 0, 1) forwards, initialDelay 0.2s 2.2s forwards, initialTransform 0.6s 2.4s cubic-bezier(0.5, 0, 0, 1) forwards, hideAfterTransform 0s 3s forwards'
          : 'none'};
    }

    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 50%;
      transform: translateX(calc(-50% + var(--move-from, 0px)));
      width: 7px;
      height: 7px;
      max-width: var(--tab-label-width, 100%);
      border-radius: 7px;
      background-color: var(--indicator-color);
      opacity: 0.7;
      display: ${({ isTransitioning, active }) => (isTransitioning && active ? 'block' : 'none')};
      animation: ${({ isTransitioning, active }) =>
        isTransitioning && active
          ? 'moveToTabRect 0.8s cubic-bezier(0.5, 0, 0, 1) forwards, returnToCircle 0.6s 0.8s cubic-bezier(0.5, 0, 0, 1) forwards'
          : 'none'};
      /* Force Safari to recalculate positioning when width changes */
      will-change: width, height, transform, border-radius;
    }
  }

  /* Show SVG circle when not animating initially - handled via opacity property */

  /* Initial and transition animations handled via ::before and ::after pseudo-elements above */
`;

// Define keyframes globally to avoid Linaria processing issues
const keyframesStyle = `
  @keyframes initialAppear {
    from {
      opacity: 0;
      width: 0;
      height: 2px;
      border-radius: 2px;
    }
    to {
      opacity: 0.7;
      width: var(--tab-label-width, 100%);
      height: 2px;
      border-radius: 2px;
    }
  }

  @keyframes initialDelay {
    from {
      opacity: 0.7;
      width: var(--tab-label-width, 100%);
      height: 2px;
      border-radius: 2px;
    }
    to {
      opacity: 0.7;
      width: var(--tab-label-width, 100%);
      height: 2px;
      border-radius: 2px;
    }
  }

  @keyframes initialTransform {
    from {
      width: var(--tab-label-width, 100%);
      height: 2px;
      border-radius: 2px;
      opacity: 0.7;
    }
    to {
      width: 7px;
      height: 7px;
      border-radius: 7px;
      opacity: 0.7;
    }
  }

  @keyframes showCircle {
    0% {
      opacity: 0;
      transform: translateX(-50%) translateZ(0);
      visibility: visible;
    }
    100% {
      opacity: 1;
      transform: translateX(-50%) translateZ(0);
      visibility: visible;
    }
  }

  @keyframes hideAfterTransform {
    to {
      opacity: 0;
      width: 0;
      height: 0;
    }
  }

  @keyframes moveToTabRect {
    from {
      transform: translateX(calc(-50% + var(--move-from, 0px)));
      width: 7px;
      height: 7px;
      border-radius: 7px;
    }
    to {
      transform: translateX(calc(var(--rect-offset, -50%) + var(--move-to, 0px)));
      width: var(--tab-label-width, 100%);
      height: 2px;
      border-radius: 2px;
    }
  }
  
  @keyframes hideDuringTransition {
    0% {
      opacity: 1;
      transform: translateX(-50%) translateZ(0);
    }
    100% {
      opacity: 0;
      transform: translateX(-50%) translateZ(0);
    }
  }
  
  @keyframes showAfterTransition {
    0% {
      opacity: 0;
      transform: translateX(-50%) translateZ(0);
      visibility: visible;
    }
    100% {
      opacity: 1;
      transform: translateX(-50%) translateZ(0);
      visibility: visible;
    }
  }

  @keyframes transformToRectangleRect {
    from {
      width: 7px;
      height: 7px;
      border-radius: 7px;
    }
    to {
      width: var(--tab-label-width, 100%);
      height: 7px;
      border-radius: 7px;
    }
  }

  @keyframes returnToCircle {
    from {
      transform: translateX(calc(var(--rect-offset, -50%) + var(--move-to, 0px)));
      width: var(--tab-label-width, 100%);
      height: 2px;
      border-radius: 2px;
    }
    to {
      transform: translateX(calc(-50% + var(--move-to, 0px)));
      width: 7px;
      height: 7px;
      border-radius: 7px;
    }
  }
`;

// Inject keyframes into document head
if (typeof document !== 'undefined') {
  const styleId = 'bull-icon-keyframes';
  if (!document.getElementById(styleId)) {
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = keyframesStyle;
    document.head.appendChild(style);
  }
}

export const StyledFilterToggleContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0 24px 16px 24px;
  opacity: 0;
  animation: fadeIn 0.3s cubic-bezier(0.33, 0, 0.3, 1) forwards;
  animation-delay: 0.46s;

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

export const StyledSwipeableContent = styled.div<{
  swipeDirection: 'left' | 'right' | null;
  isAnimating: boolean;
}>`
  position: relative;
  overflow: hidden;

  @keyframes slideInFromRight {
    from {
      transform: translateX(100%);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }

  @keyframes slideInFromLeft {
    from {
      transform: translateX(-100%);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }

  & > * {
    animation: ${({ swipeDirection, isAnimating }) => {
      if (!isAnimating || !swipeDirection) return 'none';
      return swipeDirection === 'left'
        ? 'slideInFromRight 0.3s ease-out forwards'
        : 'slideInFromLeft 0.3s ease-out forwards';
    }};
  }
`;

export const StyledSlidingWrapper = styled.div`
  width: 100%;
  overflow: hidden;
  position: relative;
  touch-action: pan-y pinch-zoom;
`;

export const StyledSlidingContainer = styled.div<{
  activeIndex: number;
  panelCount?: number;
}>`
  display: flex;
  width: ${({ panelCount = 2 }) => panelCount * 100}%;
  transform: translateX(${({ activeIndex, panelCount = 2 }) => activeIndex * (-100 / panelCount)}%);
  transition: transform 0.35s cubic-bezier(0.4, 0, 0.2, 1);
  will-change: transform;
`;

export const StyledSlidingPanel = styled.div<{
  panelCount?: number;
  isDark?: boolean;
}>`
  width: ${({ panelCount = 2 }) => 100 / panelCount}%;
  flex-shrink: 0;
  min-height: 100px;
  background: ${({ isDark }) => (isDark ? COLORS.black[100] : COLORS.white[100])};
`;
